import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Landmark, Loader2, RefreshCw, History, ExternalLink, BellRing, Send, AlertTriangle, Eye, Activity } from 'lucide-react';
import { DigestPreviewDialog } from './DigestPreviewDialog';
import { JustificationLibraryEditor } from './JustificationLibraryEditor';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SHOWN = ['USD', 'EUR', 'GBP', 'NZD', 'CAD', 'JPY', 'SGD', 'HKD', 'CHF', 'INR'];

/** Tiny inline 90-day trend (1 CCY in A$ terms so up = currency strengthening). */
const Spark = ({ values }) => {
  if (!values || values.length < 3) return null;
  const w = 56, h = 14;
  const min = Math.min(...values), max = Math.max(...values);
  if (max - min === 0) return null;
  const span = max - min;
  const pts = values.map((v, i) =>
    `${(i / (values.length - 1)) * w},${h - 1 - ((v - min) / span) * (h - 2)}`).join(' ');
  const up = values[values.length - 1] >= values[0];
  return (
    <svg width={w} height={h} className="inline-block align-middle ml-1" aria-hidden="true">
      <polyline points={pts} fill="none" strokeWidth="1.2"
        stroke={up ? '#059669' : '#E11D48'} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

/** Admin FX rates — ECB auto-fetch (base AUD) with manual overrides. */
export const FxRatesCard = () => {
  const [data, setData] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [busy, setBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [alertPct, setAlertPct] = useState('');
  // v385 — weekly rate-card drift digest settings
  const [drift, setDrift] = useState(null);
  const [driftPct, setDriftPct] = useState('');
  const [driftDay, setDriftDay] = useState('mon');
  const [benchDelta, setBenchDelta] = useState('');
  const [shiftDelta, setShiftDelta] = useState('');
  const [previewKind, setPreviewKind] = useState(null);
  const [approvalAud, setApprovalAud] = useState('');
  const [shift, setShift] = useState(null);
  const loadShift = () => axios.get(`${API_URL}/api/insights/margin/factor-shift`, { withCredentials: true })
    .then(r => setShift(r.data)).catch(() => setShift(null));
  useEffect(() => { loadShift(); }, []);
  const checkShiftNow = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/insights/margin/factor-shift/check`, {}, { withCredentials: true });
      setShift(r.data);
      if (r.data.alerted_factors?.length) toast.success(`Factor shift alert sent to ${r.data.recipients_notified} leader(s): ${r.data.alerted_factors.join(', ')}`);
      else toast.info(`No factor moved ≥ ${r.data.delta} in the last ${r.data.window_days} days`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not run factor shift check'); } finally { setBusy(false); }
  };
  useEffect(() => {
    axios.get(`${API_URL}/api/insights/margin/drift-settings`, { withCredentials: true })
      .then(r => { setDrift(r.data); setDriftPct(String(r.data.threshold_pct ?? '')); setDriftDay(r.data.digest_day || 'mon'); setBenchDelta(String(r.data.benchmark_delta ?? '')); setShiftDelta(String(r.data.factor_shift_delta ?? '')); setApprovalAud(r.data.reprice_approval_aud == null ? '' : String(r.data.reprice_approval_aud)); })
      .catch(() => setDrift({ unavailable: true }));
  }, []);
  const saveDrift = async (patch = {}) => {
    setBusy(true);
    try {
      const body = { threshold_pct: Number(driftPct), digest_day: driftDay, benchmark_delta: Number(benchDelta), factor_shift_delta: Number(shiftDelta), reprice_approval_aud: approvalAud === '' ? null : Number(approvalAud), ...patch };
      const r = await axios.put(`${API_URL}/api/insights/margin/drift-settings`, body, { withCredentials: true });
      setDrift(r.data); setDriftPct(String(r.data.threshold_pct)); setDriftDay(r.data.digest_day); setBenchDelta(String(r.data.benchmark_delta)); setShiftDelta(String(r.data.factor_shift_delta));
      toast.success(r.data.enabled ? `Drift digest: ${r.data.threshold_pct}% threshold, every ${r.data.digest_day.toUpperCase()}` : 'Drift digest paused');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save drift settings'); } finally { setBusy(false); }
  };
  const [digestLog, setDigestLog] = useState(null);
  const [resending, setResending] = useState(null);
  const loadDigestLog = () => axios.get(`${API_URL}/api/insights/margin/digest-log?limit=12`, { withCredentials: true })
    .then(r => setDigestLog(r.data.entries || [])).catch(() => setDigestLog([]));
  useEffect(() => { loadDigestLog(); }, []);
  const resendDigest = async (entry) => {
    setResending(entry.id);
    try {
      const r = await axios.post(`${API_URL}/api/insights/margin/digest-log/${entry.id}/resend`, {}, { withCredentials: true });
      if (r.data.sent) toast.success(`Resent ${entry.kind === 'deal_pack' ? 'deal pack' : `${entry.kind.toUpperCase()} digest`} to ${Array.isArray(r.data.recipients) ? r.data.recipients.length : r.data.recipients} recipient(s)`);
      else toast.error(`Resend failed — ${r.data.reason}`);
      loadDigestLog();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not resend'); } finally { setResending(null); }
  };
  const sendDriftDigest = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/insights/margin/send-drift-digest`, {}, { withCredentials: true });
      if (r.data.sent) toast.success(`Drift digest sent to ${r.data.recipients} leadership recipient(s)`);
      else toast.info(`Digest not sent — ${r.data.reason}`);
      loadDigestLog();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send drift digest'); } finally { setBusy(false); }
  };
  // Mirrors the admin Currencies list (excl. AUD base) so every configured
  // currency gets an FX row; falls back to the classic set if unavailable.
  const [shown, setShown] = useState(SHOWN);
  // ascending 90-day A$-per-unit series per currency for the sparklines
  const [trend, setTrend] = useState({});

  const openHistory = async () => {
    setHistoryOpen(true);
    if (!history) {
      try {
        const res = await axios.get(`${API_URL}/api/fx/history?days=90`, { withCredentials: true });
        setHistory(res.data);
      } catch { setHistory({ daily: [], override_changes: [] }); }
    }
  };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/fx/rates`, { withCredentials: true });
      setData(res.data);
      setOverrides(res.data.overrides || {});
      setAlertPct(res.data.alert_threshold_pct ?? '');
    } catch { /* card stays in loading state */ }
    try {
      const cur = await axios.get(`${API_URL}/api/settings/currencies`, { withCredentials: true });
      const codes = (cur.data?.currencies || [])
        .map(c => (c.code || '').trim().toUpperCase())
        .filter(c => c && c !== 'AUD');
      if (codes.length) setShown([...new Set(codes)]);
    } catch { /* keep fallback list */ }
    try {
      const h = await axios.get(`${API_URL}/api/fx/history?days=90`, { withCredentials: true });
      const daily = [...(h.data?.daily || [])].sort((a, b) => (a.date < b.date ? -1 : 1));
      const series = {};
      daily.forEach(d => Object.entries(d.rates || {}).forEach(([c, v]) => {
        if (v) (series[c] = series[c] || []).push(1 / Number(v));
      }));
      setTrend(series);
      setHistory(h.data);
    } catch { /* sparklines stay hidden */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/fx/refresh`, {}, { withCredentials: true });
      toast.success('ECB rates refreshed');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refresh failed');
    } finally { setBusy(false); }
  };

  const saveOverrides = async () => {
    setBusy(true);
    try {
      const clean = {};
      Object.entries(overrides).forEach(([k, v]) => {
        const n = parseFloat(v);
        clean[k] = Number.isFinite(n) && n > 0 ? n : null;
      });
      const res = await axios.put(`${API_URL}/api/fx/overrides`, { overrides: clean }, { withCredentials: true });
      toast.success('FX overrides saved');
      setData(d => ({ ...d, effective: res.data.effective, overrides: res.data.overrides }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save overrides');
    } finally { setBusy(false); }
  };

  const saveThreshold = async () => {
    setBusy(true);
    try {
      const res = await axios.put(`${API_URL}/api/fx/alert-threshold`,
        { pct: alertPct === '' ? null : alertPct }, { withCredentials: true });
      toast.success(res.data.alert_threshold_pct != null
        ? `FX movement alerts on — threshold ${res.data.alert_threshold_pct}%`
        : 'FX movement alerts turned off');
      setData(d => ({ ...d, alert_threshold_pct: res.data.alert_threshold_pct }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save threshold');
    } finally { setBusy(false); }
  };

  const sendDigest = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/fx/send-movement-digest`, {}, { withCredentials: true });
      if (r.data.sent) toast.success(`Digest sent — ${r.data.count} alert(s) emailed to ${r.data.recipients} leadership recipient(s)`);
      else toast(`Digest not sent — ${r.data.reason}`);
      loadDigestLog();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send digest');
    } finally { setBusy(false); }
  };

  return (
    <Card className="border-slate-200" data-testid="fx-rates-card">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Landmark className="w-4 h-4 text-[#4ABFCF]" />
            <h3 className="text-sm font-semibold text-[#0F2040]">Currency Conversion (base AUD)</h3>
          </div>
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="ghost" onClick={openHistory} data-testid="fx-history-btn" className="text-slate-500">
              <History className="w-3 h-3 mr-1" /> Rate history
            </Button>
            <Button size="sm" variant="outline" onClick={refresh} disabled={busy} data-testid="fx-refresh-btn">
              <RefreshCw className="w-3 h-3 mr-1" /> Refresh ECB rates
            </Button>
          </div>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          1 AUD = X currency. ECB rates auto-refresh daily (currencies outside the ECB set, like AED, use an open reference feed); a manual override wins until cleared (blank = use ECB).
          Estimates snapshot their AUD rate when saved; reporting uses the snapshot.
          {data?.fetched_at ? <> Last fetched {new Date(data.fetched_at).toLocaleString()}.</> : null}
        </p>
        {!data ? (
          <div className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-5 gap-2">
              {shown.map(c => (
                <div key={c}>
                  <div className="text-[10px] font-semibold text-slate-500">{c}
                    {(data.supplemental_codes || []).includes(c) ? (
                      <span className="ml-1 font-normal text-slate-400" title="The ECB doesn't publish this currency — sourced from an open exchange-rates feed"
                        data-testid={`fx-supplemental-${c.toLowerCase()}`}>
                        Ref {data.rates?.[c] != null ? Number(data.rates[c]).toFixed(4) : '—'}
                      </span>
                    ) : (
                      <span className="ml-1 font-normal text-slate-400">ECB {data.rates?.[c] != null ? Number(data.rates[c]).toFixed(4) : '—'}</span>
                    )}
                    <span title={`90-day trend of 1 ${c} in A$`} data-testid={`fx-spark-${c.toLowerCase()}`}><Spark values={trend[c]} /></span>
                  </div>
                  <Input value={overrides[c] ?? ''} placeholder={data.effective?.[c] != null ? Number(data.effective[c]).toFixed(4) : ''}
                    onChange={e => setOverrides(prev => ({ ...prev, [c]: e.target.value }))}
                    className="h-7 mt-0.5 text-xs font-mono" data-testid={`fx-override-${c.toLowerCase()}`} />
                  <div className="text-[9px] text-slate-400 mt-0.5 font-mono" data-testid={`fx-aud-equiv-${c.toLowerCase()}`}>
                    1 {c} = A${data.effective?.[c] ? (1 / Number(data.effective[c])).toFixed(4) : '—'}
                  </div>
                  {(data.missing_rates || []).includes(c) && (
                    <div className="text-[9px] text-amber-600 mt-0.5 font-medium" data-testid={`fx-missing-${c.toLowerCase()}`}>
                      No ECB feed — set an override
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between">
              <Button onClick={saveOverrides} disabled={busy} size="sm"
                className="bg-[#0F2040] hover:bg-[#1a3156] text-white" data-testid="fx-overrides-save-btn">
                {busy ? 'Saving…' : 'Save Overrides'}
              </Button>
              <a href={data.ecb_url || 'https://www.ecb.europa.eu/stats/policy_and_exchange_rates/euro_reference_exchange_rates/html/index.en.html'}
                target="_blank" rel="noreferrer"
                className="text-[10px] text-[#4ABFCF] hover:underline flex items-center gap-1" data-testid="fx-ecb-link">
                ECB reference rates <ExternalLink className="w-2.5 h-2.5" />
              </a>
            </div>
            {/* v380 — FX movement alert threshold + manual digest trigger */}
            <div className="border-t border-slate-100 pt-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-[#0F2040]">
                    <BellRing className="w-3.5 h-3.5 text-[#4ABFCF]" /> Movement alert threshold
                  </div>
                  <p className="text-[10px] text-slate-400 mt-0.5 max-w-md">
                    Flags open estimates whose currency moved more than this % vs their FX snapshot —
                    shown on Insights → Margin and emailed daily to leadership. Blank = alerts off.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Input value={alertPct} type="number" min="0.1" max="50" step="0.5"
                      onChange={e => setAlertPct(e.target.value)} placeholder="e.g. 5"
                      className="h-7 w-24 text-xs font-mono pr-5" data-testid="fx-alert-threshold-input" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">%</span>
                  </div>
                  <Button size="sm" variant="outline" onClick={saveThreshold} disabled={busy}
                    data-testid="fx-alert-threshold-save-btn">Save</Button>
                  <Button size="sm" variant="ghost" onClick={() => setPreviewKind('fx')} disabled={busy} className="text-slate-500" title="Preview the FX digest before sending" data-testid="fx-preview-digest-btn">
                    <Eye className="w-3 h-3 mr-1" /> Preview
                  </Button>
                  <Button size="sm" variant="ghost" onClick={sendDigest}
                    disabled={busy || data.alert_threshold_pct == null}
                    title={data.alert_threshold_pct == null ? 'Set and save a threshold first' : 'Email the current FX movement digest to leadership now'}
                    className="text-slate-500" data-testid="fx-send-digest-btn">
                    <Send className="w-3 h-3 mr-1" /> Send digest now
                  </Button>
                </div>
              </div>
            </div>
            {drift && !drift.unavailable && (
              <div className="border-t border-slate-100 pt-3" data-testid="drift-digest-settings">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-[#0F2040]">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> Rate-card drift digest
                    </div>
                    <p className="text-[10px] text-slate-400 mt-0.5 max-w-md">
                      Weekly email to leadership listing open estimates whose saved price differs from today's rate cards by more than this %.
                      {drift.last_sent_date ? ` Last sent ${drift.last_sent_date}.` : ''}
                    </p>
                    {drift.next_digest && (
                      <p className={`text-[11px] mt-1 font-medium ${drift.enabled ? 'text-[#0F2040]' : 'text-slate-400'}`} data-testid="drift-next-digest">
                        {drift.enabled ? 'Next digest' : 'Paused — would next send'}: <b>{new Date(`${drift.next_digest.date}T00:00:00Z`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })}</b>
                        {drift.next_digest.days_ahead === 0 ? ' (today)' : ` (in ${drift.next_digest.days_ahead} day${drift.next_digest.days_ahead === 1 ? '' : 's'})`}
                        {' · '}<span className={drift.next_digest.count ? 'text-amber-700' : 'text-slate-500'} data-testid="drift-next-digest-count">{drift.next_digest.count} estimate{drift.next_digest.count === 1 ? '' : 's'} would be listed today</span>
                        {drift.next_digest.count > 0 && <span className="text-slate-400"> · ≈A${Number(drift.next_digest.aud_impact).toLocaleString()} impact</span>}
                        {drift.next_digest.count === 0 && <span className="text-slate-400"> — nothing will be sent unless drift appears</span>}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Input value={driftPct} type="number" min="0.1" max="100" step="0.5"
                        onChange={e => setDriftPct(e.target.value)} placeholder="e.g. 2"
                        className="h-7 w-24 text-xs font-mono pr-5" data-testid="drift-threshold-input" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">%</span>
                    </div>
                    <select value={driftDay} onChange={e => setDriftDay(e.target.value)}
                      className="h-7 text-xs border border-slate-200 rounded-md px-2 bg-white text-slate-700" data-testid="drift-day-select">
                      {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map(d => <option key={d} value={d}>{d[0].toUpperCase() + d.slice(1)}</option>)}
                    </select>
                    <Button size="sm" variant="outline" onClick={() => saveDrift()} disabled={busy} data-testid="drift-settings-save-btn">Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => saveDrift({ enabled: !drift.enabled })} disabled={busy}
                      className={drift.enabled ? 'text-slate-500' : 'text-amber-700'} data-testid="drift-digest-toggle-btn">
                      {drift.enabled ? 'Pause' : 'Resume'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setPreviewKind('drift')} disabled={busy} className="text-slate-500" title="Preview the drift digest before sending" data-testid="drift-preview-digest-btn">
                      <Eye className="w-3 h-3 mr-1" /> Preview
                    </Button>
                    <Button size="sm" variant="ghost" onClick={sendDriftDigest} disabled={busy} className="text-slate-500" data-testid="drift-send-digest-btn">
                      <Send className="w-3 h-3 mr-1" /> Send now
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3 mt-3" data-testid="benchmark-delta-settings">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-[#0F2040]">
                      <AlertTriangle className="w-3.5 h-3.5 text-violet-500" /> Benchmark alert delta
                    </div>
                    <p className="text-[10px] text-slate-400 mt-0.5 max-w-md">
                      A deal is flagged (Margin tab, deal summary and the Send-to-Stakeholders quote gate) when any project factor multiplier sits at least this far above the portfolio average across open deals.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">+</span>
                      <Input value={benchDelta} type="number" min="0.01" max="2" step="0.01"
                        onChange={e => setBenchDelta(e.target.value)} placeholder="e.g. 0.15"
                        className="h-7 w-24 text-xs font-mono pl-5" data-testid="benchmark-delta-input" />
                    </div>
                    <Button size="sm" variant="outline" onClick={() => saveDrift()} disabled={busy} data-testid="benchmark-delta-save-btn">Save</Button>
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3 mt-3" data-testid="factor-shift-settings">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-[#0F2040]">
                      <Activity className="w-3.5 h-3.5 text-rose-500" /> Factor shift alert
                    </div>
                    <p className="text-[10px] text-slate-400 mt-0.5 max-w-md">
                      CRO, CCO and Super Admin get an in-app alert when a portfolio factor average moves by at least this much within {shift?.window_days ?? 30} days (checked every 6 hours; one alert per factor per week).
                      {shift?.last_checked ? ` Last checked ${new Date(shift.last_checked).toLocaleString()}.` : ''}
                    </p>
                    {shift?.rows?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1" data-testid="factor-shift-chips">
                        {shift.rows.filter(r => r.change != null && r.change !== 0).slice(0, 6).map(r => (
                          <span key={r.key} className={`text-[10px] px-1.5 py-0 rounded border ${r.shifted ? 'bg-rose-50 border-rose-200 text-rose-700 font-semibold' : 'bg-slate-50 border-slate-200 text-slate-500'}`} data-testid={`factor-shift-chip-${r.key}`}>
                            {r.label} ×{r.then.toFixed(2)} → ×{r.now.toFixed(2)} ({r.change > 0 ? '+' : ''}{r.change.toFixed(2)})
                          </span>
                        ))}
                        {shift.rows.every(r => !r.change) && <span className="text-[10px] text-slate-400">No movement in the window.</span>}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">±</span>
                      <Input value={shiftDelta} type="number" min="0.01" max="1" step="0.01"
                        onChange={e => setShiftDelta(e.target.value)} placeholder="e.g. 0.05"
                        className="h-7 w-24 text-xs font-mono pl-5" data-testid="factor-shift-delta-input" />
                    </div>
                    <Button size="sm" variant="outline" onClick={() => saveDrift()} disabled={busy} data-testid="factor-shift-save-btn">Save</Button>
                    <Button size="sm" variant="ghost" onClick={checkShiftNow} disabled={busy} className="text-slate-500" data-testid="factor-shift-check-btn">
                      <BellRing className="w-3 h-3 mr-1" /> Check now
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-600 border-t border-slate-100 pt-2" data-testid="reprice-approval-row">
                  <span className="font-semibold text-[#0F2040]">Bulk reprice approval</span>
                  <span>Require a second leader when the combined price swing exceeds</span>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">A$</span>
                    <Input value={approvalAud} type="number" min="1" step="1000" onChange={e => setApprovalAud(e.target.value)} placeholder="off"
                      className="h-7 w-28 text-xs font-mono pl-6" data-testid="reprice-approval-aud-input" />
                  </div>
                  <Button size="sm" variant="outline" onClick={() => saveDrift()} disabled={busy} data-testid="reprice-approval-save-btn">Save</Button>
                  <span className="text-[10px] text-slate-400">Blank = no approval needed.</span>
                </div>
                <JustificationLibraryEditor />
                <DigestPreviewDialog kind={previewKind} open={!!previewKind} onClose={() => setPreviewKind(null)} sending={busy}
                  onSend={async () => { if (previewKind === 'fx') await sendDigest(); else await sendDriftDigest(); setPreviewKind(null); }} />
                {digestLog && (
                  <div className="mt-3" data-testid="digest-history">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Digest history <span className="font-normal normal-case">· FX, drift, factor-shift and deal-pack emails</span></p>
                    {digestLog.length === 0 ? (
                      <p className="text-[11px] text-slate-400" data-testid="digest-history-empty">No digests sent yet.</p>
                    ) : (
                      <ul className="divide-y divide-slate-100 rounded border border-slate-200 max-h-40 overflow-y-auto">
                        {digestLog.map((d, i) => (
                          <li key={i} className="flex items-center justify-between gap-3 px-2.5 py-1.5 text-[11px]" data-testid={`digest-history-row-${i}`}>
                            <span className="inline-flex items-center gap-2 min-w-0">
                              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${d.sent ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                              <span className="font-semibold text-[#0F2040] uppercase">{d.kind === 'fx' ? 'FX' : d.kind === 'drift' ? 'Drift' : d.kind === 'factor_shift' ? 'Factor shift' : d.kind === 'security' ? 'Security' : 'Deal pack'}</span>
                              <span className="text-slate-500 truncate">{d.sent ? `${d.count} item(s) → ${d.recipient_count} recipient(s)` : `failed — ${(d.reason || '').slice(0, 80)}`}</span>
                            </span>
                            <span className="text-slate-400 shrink-0" title={(d.recipients || []).join(', ')}>{d.at ? new Date(d.at).toLocaleString() : ''}</span>
                            {!d.sent && d.kind !== 'factor_shift' && (
                              <button type="button" onClick={() => resendDigest(d)} disabled={resending === d.id}
                                className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold text-[#0F2040] border border-slate-200 rounded px-1.5 py-0.5 hover:bg-slate-50 disabled:opacity-50"
                                title={d.retried_at ? `Last retried ${new Date(d.retried_at).toLocaleString()}` : 'Retry this send'} data-testid={`digest-history-resend-${i}`}>
                                {resending === d.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Resend
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
          <DialogContent className="max-w-3xl bg-white max-h-[80vh] overflow-y-auto" data-testid="fx-history-dialog">
            <DialogHeader><DialogTitle className="text-[#0F2040] text-base">FX rate history — 1 AUD = X (last 90 days)</DialogTitle></DialogHeader>
            {!history ? (
              <div className="flex items-center gap-2 text-xs text-slate-400 py-6"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
            ) : (
              <div className="space-y-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                        <th className="py-1 pr-2">Date</th>
                        {shown.slice(0, 7).map(c => <th key={c} className="py-1 pr-2 text-right">{c}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {(history.daily || []).map(d => (
                        <tr key={d.date} className="border-b border-slate-100" data-testid={`fx-history-row-${d.date}`}>
                          <td className="py-1 pr-2 font-mono text-slate-600">{d.date}</td>
                          {shown.slice(0, 7).map(c => (
                            <td key={c} className="py-1 pr-2 text-right font-mono">{d.rates?.[c] != null ? Number(d.rates[c]).toFixed(4) : '—'}</td>
                          ))}
                        </tr>
                      ))}
                      {(history.daily || []).length === 0 && (
                        <tr><td colSpan={8} className="py-4 text-center text-slate-400 text-xs">No stored snapshots yet — they accumulate with each daily ECB refresh.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {(history.override_changes || []).length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Override changes</p>
                    {(history.override_changes || []).map((o, i) => (
                      <p key={i} className="text-[11px] text-slate-600" data-testid={`fx-override-audit-${i}`}>
                        <span className="font-mono text-slate-400">{(o.at || '').slice(0, 16).replace('T', ' ')}</span>
                        {' '}— {o.updated_by || 'admin'}: {Object.keys(o.after || {}).length
                          ? Object.entries(o.after).map(([k, v]) => `${k}=${v}`).join(', ')
                          : 'all overrides cleared'}
                      </p>
                    ))}
                  </div>
                )}
                <a href={history.ecb_url} target="_blank" rel="noreferrer"
                  className="text-[10px] text-[#4ABFCF] hover:underline flex items-center gap-1 w-fit">
                  Source: ECB euro reference rates <ExternalLink className="w-2.5 h-2.5" />
                </a>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default FxRatesCard;
